import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, signal, Signal, viewChild, ViewChild, WritableSignal } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, debounceTime, map, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { InventoryAction, providersTaskTypes } from '../../models/inventory_action.model';
import { Warehouse } from '../../models/warehouse.model';
import { Provider } from '../../models/provider.model';
import { MatSort } from '@angular/material/sort';
import { FormBuilder } from '@angular/forms';
import { ProviderService } from '../../providers/provider.service';
import { WarehousesService } from '../../services/warehouses.service';
import { AdminService } from '../../admin/services/admin.service';
import { InventoryActionService } from '../../services/inventory-action.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StatusesService } from '../../services/statuses.service';
import { Status } from '../../models/status.model';
import { toUnderscore } from '../../utils/string-utils';
import { Status as FolderStatus, StatusColor as FolderStatusColor } from '../../models/folder.model';
import { MatPaginator } from '@angular/material/paginator';
import { RoleService } from '../../role/role.service';

@Component({
    selector: 'app-inventory-pricings',
    templateUrl: './inventory-pricings.component.html',
    styleUrls: ['./inventory-pricings.component.scss', '../actions/inventory-actions.component.scss', '../inventory.component.scss'],
    standalone: false
})
export class InventoryPricingsComponent implements AfterViewInit {
  types = providersTaskTypes;
  
  private dataChanged$ = new Subject<boolean>();

  dataSource = new MatTableDataSource<InventoryAction>();

  displayedColumns: string[] = [
    'type',
    'correlative_id',
    'created_at',
    'estimated_completion_date',
    'owner',
    'total_quantity',
    'answered',
    'status',
    'warehouse_name',
    'actions',
  ];

  warehouses: Warehouse[];

  inventoryActions = [];

  isLoading = signal(true);

  pageSizes = [10, 25, 50, 100];

  sort = 'updated_at desc';

  totalData = 0;

  page = 0;

  totalPages = 0;

  loadingMore = false;

  statuses: WritableSignal<Status[]> = signal([]);

  @ViewChild(MatSort) sorter: MatSort;

  private paginator = viewChild<MatPaginator>('paginator');

  private readonly _cdr = inject(ChangeDetectorRef);

  private readonly fb = inject(FormBuilder);

  filters = this.fb.group({
    created_at_start: this.fb.control(''),
    created_at_end: this.fb.control(''),
    query: this.fb.control(''),
    'owner_id[]': this.fb.control([]),
    'owner_type': this.fb.control('Provider'),
    'status_id[]': this.fb.control([]),
    'type': this.fb.control(''),
    'warehouse_ids[]': this.fb.control([]),
    answered: this.fb.control(null),
  });

  showFilters = signal(false);

  private readonly adminService = inject(AdminService);
  
  private readonly destroyRef = inject(DestroyRef);
  
  private readonly inventoryActionService = inject(InventoryActionService);
  
  private readonly providerService = inject(ProviderService);
  providers: Signal<Provider[]> = this.providerService.providers;
  
  private readonly roleService = inject(RoleService);
  
  private readonly statusService = inject(StatusesService);

  private readonly warehousesService = inject(WarehousesService);


  constructor() {}

  ngAfterViewInit() {
    
    this.addRefreshTableSubscription();

    this.addInitialDataSubscription();

    this.addFilterSubscriptions();
  }

  addRefreshTableSubscription() {
    this.dataSource.sort = this.sorter;
    merge(
      this.paginator().page,
      this.sorter.sortChange.asObservable(),
      this.filters.valueChanges,
      this.dataChanged$, // This is used to refresh the data table when an action is created
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        switchMap(() => {
          this._cdr.markForCheck();
          this.isLoading.set(true);
          this.page = 1;
          return this.getTableData$(
            (this.paginator()?.pageIndex ?? 0) + 1,
            (this.paginator()?.pageSize ?? 10),
            this.sort,
            this.filters.getRawValue(),
          ).pipe(catchError(() => of(null)));
        }),
        map((data) => {
          this._cdr.markForCheck();
          if (data == null) return [];
          this.totalData = data.total_count;
          this.totalPages = data.total_pages;
          this.isLoading.set(false);
          return data.items;
        }),
      )
      .subscribe((data) => {
        this._cdr.markForCheck();
        this.dataSource = new MatTableDataSource(this.prepareDataSource(data));
      });
  }

  addInitialDataSubscription() {
    this._cdr.markForCheck();
    this.isLoading.set(true);
    this.warehousesService.index().subscribe((warehouses) => {
      this._cdr.markForCheck();
      this.isLoading.set(false);
      if (!warehouses?.length) return;

      this.warehouses = warehouses;
      this.filters.get('warehouse_ids[]').setValue(this.warehouses?.map((w) => w.id) || []);
    });
  }

  addFilterSubscriptions() {
    this.filters.get('type').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((type) => {
      this._cdr.markForCheck();
      if (type) {
        this.statusService.statuses('Status::' + type).subscribe((statuses) => {
          this.statuses.set(statuses);
          this.filters.get('status_id[]').setValue(statuses.map((status) => status.id));
        });
      } else {
        this.statuses.set([]);
        this.filters.get('status_id[]').setValue([]);
      }
    });
  }

  getTableData$(pageNumber: number, pageSize: number, sort: string, filters) {
    return this.inventoryActionService.index(
      pageNumber,
      pageSize,
      sort,
      filters,
      'providers_tasks',
    );
  }
  
  onSortChange({ active, direction }: { active: string; direction: string }) {
    if (!direction) {
      this.sort = 'updated_at desc';
      return;
    }

    this.sort = `${active} ${direction}`;
  }

  private prepareDataSource(data: InventoryAction[]): InventoryAction[] {

    // Group items that have the same folder_id
    const foldersIds = {};
    return data.filter((item) => {
      if (!item.folder_id) return true;

      if (!foldersIds[item.folder_id]) {
        foldersIds[item.folder_id] = true;
        item.id = item.folder_id;
        item.type = 'Folder::InventoryAction::Pricing';
        item.correlative_id = item.folder.correlative_id;
        item.status = { 
          name: FolderStatus[item.folder.status],
          color: FolderStatusColor[item.folder.status],
         };
        item.owner.name = `${item.folder.extra_params.documents_count} ${this.adminService.getText('provider_plural')}`;
        item.owner.tooltip = item.folder.extra_params.provider_names;
        return true;
      }

      return false;
    });
  };

  reload() {
    this.dataChanged$.next(true);
  }

  getMore() {
    this._cdr.markForCheck();
    this.loadingMore = true;
    this.page = this.page + 1;
    this.getTableData$(this.page, 20, this.sort, this.filters.getRawValue())
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        this._cdr.markForCheck();
        this.loadingMore = false;
        if (data == null) return;
        this.dataSource.data = this.dataSource.data.concat(data.items);
      });
  }

  titleForType(type: string) {
    return this.adminService.getText(toUnderscore(type));
  }
  
  
  iconForType(type: string): string {
    switch (type) {
      case 'InventoryAction::BuyOrder':
        return 'list_alt';
      case 'InventoryAction::Pricing':
        return 'request_quote';
      case 'Folder::InventoryAction::Pricing':
        return 'folder';
      default:
        return 'add'; // Valor por defecto
    }
  }

  canEdit(item: InventoryAction): Observable<boolean> {
    if (item.type === 'InventoryAction::BuyOrder' && item.draft && !item.finish) {
      return this.roleService.can('inventory_action', 'update');
    }

    return of(false);
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  clearSearch() {
    this._cdr.markForCheck();
    this.filters.get('query').setValue('');
  }
}
